import { useEffect, useState } from 'react';
import { Alert, Box, Skeleton, Stack, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import { client, type Incident, type ServiceStatus } from '../lib/api';
import { ServiceStatusChip } from '../components/StatusChip';
import { PageHeader, Section } from '../components/Page';
import { tokens } from '../theme';

const OVERALL = {
  outage: { label: 'Major outage', bg: tokens.errorSoft, line: '#FECDCA', fg: '#7A271A', dot: '#D92D20', Icon: PriorityHighIcon },
  degraded: { label: 'Partial degradation', bg: tokens.warnSoft, line: '#FEDF89', fg: '#7A2E0E', dot: '#DC6803', Icon: PriorityHighIcon },
  maintenance: { label: 'Maintenance in progress', bg: tokens.tint, line: '#B9D3EE', fg: tokens.blueDeep, dot: tokens.blue, Icon: BuildOutlinedIcon },
  operational: { label: 'All systems operational', bg: tokens.successSoft, line: tokens.successLine, fg: '#054F31', dot: '#079455', Icon: CheckIcon },
} as const;

export function StatusPage() {
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client
      .status()
      .then((data) => {
        setServices(data.services);
        setIncidents(data.incidents);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const key: keyof typeof OVERALL = services.some((s) => s.status === 'outage')
    ? 'outage'
    : services.some((s) => s.status === 'degraded')
      ? 'degraded'
      : services.some((s) => s.status === 'maintenance')
        ? 'maintenance'
        : 'operational';
  const o = OVERALL[key];
  const healthy = services.filter((s) => s.status === 'operational').length;
  const lastUpdate = services.reduce<string | null>(
    (acc, s) => (!acc || s.updated_at > acc ? s.updated_at : acc),
    null,
  );

  return (
    <>
      <PageHeader
        title="Service status"
        subtitle="Live health for the portal and every IT Lab application."
        aside={
          !loading && !error ? (
            <Stack
              direction="row"
              alignItems="center"
              gap={1.75}
              aria-live="polite"
              sx={{ px: 3, py: 2.25, borderRadius: 2, bgcolor: o.bg, border: `1px solid ${o.line}`, flex: 'none' }}
            >
              <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: o.dot, color: '#fff', display: 'grid', placeItems: 'center' }}>
                <o.Icon fontSize="small" aria-hidden />
              </Box>
              <Box>
                <Typography fontWeight={700} fontSize={18} color={o.fg}>
                  {o.label}
                </Typography>
                <Typography fontSize={14} color={o.fg}>
                  {healthy} of {services.length} services healthy
                </Typography>
              </Box>
            </Stack>
          ) : undefined
        }
      />

      <Section py={{ xs: 6, md: 8 }}>
        <Stack spacing={3}>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={2} flexWrap="wrap">
            <Typography variant="h2" fontSize={28}>
              Services
            </Typography>
            {lastUpdate && (
              <Typography fontSize={14} color="text.secondary">
                Last change {new Date(lastUpdate).toLocaleString()}
              </Typography>
            )}
          </Stack>
          {error && <Alert severity="error">{error}</Alert>}
          <Box sx={{ border: `1px solid ${tokens.border}`, borderRadius: 2, overflow: 'hidden' }}>
            {loading &&
              [1, 2, 3, 4].map((i) => (
                <Box key={i} sx={{ px: 3.5, py: 3, borderBottom: `1px solid ${tokens.divider}` }}>
                  <Skeleton width="40%" />
                  <Skeleton width="25%" />
                </Box>
              ))}
            {services.map((s, i) => (
              <Stack
                key={s.id}
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ sm: 'center' }}
                gap={1.5}
                sx={{ px: 3.5, py: 3, borderTop: i ? `1px solid ${tokens.divider}` : 0 }}
              >
                <Box minWidth={0}>
                  <Typography fontWeight={700} fontSize={18} color={tokens.ink}>
                    {s.name}
                  </Typography>
                  <Typography fontSize={15} color="text.secondary">
                    {s.message || '—'}
                  </Typography>
                  <Typography fontSize={13} color={tokens.text3}>
                    Updated {new Date(s.updated_at).toLocaleString()}
                    {s.updated_by ? ` · ${s.updated_by}` : ''}
                  </Typography>
                </Box>
                <ServiceStatusChip status={s.status} />
              </Stack>
            ))}
          </Box>
        </Stack>
      </Section>

      <Section py={0} sx={{ pb: { xs: 8, md: 12 } }}>
        <Stack spacing={3}>
          <Typography variant="h2" fontSize={28}>
            Recent incidents
          </Typography>
          {!loading && incidents.length === 0 && (
            <Box sx={{ p: 5, borderRadius: 2, bgcolor: tokens.subtle, border: `1px solid ${tokens.divider}` }}>
              <Typography fontWeight={600} fontSize={18} color={tokens.ink}>
                No incidents in the last 14 days
              </Typography>
              <Typography color="text.secondary">Incidents and planned maintenance posted by IT Lab will appear here.</Typography>
            </Box>
          )}
          {incidents.map((i) => {
            const svc = services.find((s) => s.id === i.service_id);
            return (
              <Box key={i.id} sx={{ p: 3, borderRadius: 2, border: `1px solid ${tokens.border}` }}>
                <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap" mb={0.5}>
                  <Typography fontWeight={700} fontSize={17} color={tokens.ink}>
                    {i.title}
                  </Typography>
                  <Box component="span" sx={{ px: 1.25, py: 0.25, borderRadius: 999, bgcolor: tokens.strip, fontSize: 13, fontWeight: 600, color: '#344054', textTransform: 'capitalize' }}>
                    {i.severity}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      px: 1.25,
                      py: 0.25,
                      borderRadius: 999,
                      fontSize: 13,
                      fontWeight: 600,
                      bgcolor: i.resolved_at ? tokens.successSoft : tokens.warnSoft,
                      color: i.resolved_at ? tokens.success : tokens.warn,
                    }}
                  >
                    {i.resolved_at ? 'Resolved' : 'Active'}
                  </Box>
                </Stack>
                <Typography fontSize={14} color="text.secondary">
                  {svc?.name ?? 'Service'} · started {new Date(i.started_at).toLocaleString()}
                  {i.resolved_at ? ` · resolved ${new Date(i.resolved_at).toLocaleString()}` : ''}
                </Typography>
                {i.body && (
                  <Typography mt={1} color="#344054">
                    {i.body}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      </Section>
    </>
  );
}
